import { Injectable } from '@angular/core';
import { Base64Service } from './base64.service';
import { SyncDigestService } from './crypt/sync-digest.service';
import { Store } from '@ngrx/store';
import * as fromRoot from '../reducers';
import { environment } from '../../environments/environment';
import { PathListApiOutput, LinkPathListApiOutput } from '../shared/models/api';
import { HttpClient } from '@angular/common/http';
import { LoggerService } from './logger.service';
import { ERROR_CODES, ErrCode, StorageBackend } from '../shared/models';
import { ITransferItemDownload, ILinkSignParams } from 'src/sync';

@Injectable({
  providedIn: 'root',
})
export class UrlService {
  private userId: number;
  private deviceId: number;

  public downloadhosts: string[] = [];
  public uploadhosts: string[] = [];

  private hosts = {
    web: [],
    compat: [],
    preview: [],
    news: [],
  };
  private readonly excludedRedirectPaths = ['login', 'logout', 'emailchange', 'verifychangedemail', 'verifyemail'];
  constructor(
    private base64: Base64Service,
    private digest: SyncDigestService,
    private http: HttpClient,
    private log: LoggerService,
    private store: Store<fromRoot.State>,
  ) {
    this.init();
  }

  private init(): void {
    this.store.select(fromRoot.getAuthState).subscribe((data) => {
      if (data.authenticated && data.user) {
        this.userId = data.user.uid;
        this.deviceId = data.user.web_device_id;
      } else {
        this.userId = 0;
        this.deviceId = 0;
      }
    });
  }

  public setHostMulti(pathlist: PathListApiOutput | LinkPathListApiOutput): void {
    const hostTypes = ['web', 'compat', 'preview', 'news'];
    hostTypes.forEach((hostType) => {
      if (pathlist['servers_' + hostType]) {
        this.setHostByType(hostType, pathlist['servers_' + hostType]);
      }
    });
    this.log.info(`Using hosts ${JSON.stringify(this.hosts)}`);
  }

  public setHostByType(type: string, hosts: string[]): void {
    if (hosts && hosts.length) {
      hosts = hosts.map((val) => 'https://' + val);
      this.hosts[type] = hosts;
      if (type == 'web') {
        this.uploadhosts = hosts.slice(0);
        this.downloadhosts = hosts.slice(0);
      }
    }
  }

  public getHostByType(type: string): string[] {
    return this.hosts[type] || [];
  }

  /**
   * Gets the return to value from the URL and returns it as a URL Encoded
   * component to be added to the login/logout state.
   */
  public getReturnTo(): string {
    const pathname = window.location.pathname;
    const isExcludedRedirectPath = this.excludedRedirectPaths.some((path) => pathname.indexOf(path) > -1);
    if (isExcludedRedirectPath) {
      return undefined;
    }
    return encodeURIComponent(pathname + window.location.search);
  }

  public mkSubscribe(): string {
    return [environment.wakeHost, '?cid=t_', this.userId, '&timestamp=', Date.now()].join('');
  }

  public mkApi(host: string, apiName: string, queryParams = ''): string {
    return this.decorateUrl([host, apiName, queryParams]);
  }

  // called in legacy, no "references" will be found - see upload-service
  // storageBackend (from pathpreupload) is appended as the storage_backend query param.
  public mkUpload(host: string, apiName: string, storageBackend = ''): string {
    if (!host) {
      throw new ErrCode(ERROR_CODES.DOWNLOAD_INVALID_RESPONSE, 'No upload host available');
    }
    const storageBackendParam = storageBackend ? '&storage_backend=' + storageBackend : '';
    return this.decorateUrl([host, '/upload.fcgi?command=', apiName, storageBackendParam]);
  }

  public async mkDownloadMulti(): Promise<string> {
    const baseDomain = await this.requireHost('web');

    return this.decorateUrl([baseDomain, '/proxyapi.fcgi?command=downloadmulti']);
  }

  // Resolves a serverpool host of the given type, or throws if none are configured.
  // Guarantees an absolute host so URLs are never relative /mfs.
  private async requireHost(type: 'web' | 'compat' | 'preview' | 'news'): Promise<string> {
    const host = await this.getAvailableHost(type);
    if (!host) {
      throw new ErrCode(ERROR_CODES.DOWNLOAD_INVALID_RESPONSE, `No serverpool host available for type=${type}`);
    }
    return host;
  }

  // Picks an up host via isup.txt; on exhaustion falls back to the first host
  // (never relative). Returns null only when no hosts are configured.
  private async getAvailableHost(type: 'web' | 'compat' | 'preview' | 'news', retry = 0): Promise<string | null> {
    const hosts = this.hosts[type];
    if (!hosts?.length) {
      this.log.error(`No hosts configured for type=${type}`);
      return null;
    }

    try {
      this.log.info(`Make download ${type} host: ${hosts[retry]}`);
      await this.http.get(hosts[retry] + '/isup.txt', { responseType: 'text' }).toPromise();
      return hosts[retry];
    } catch (ex) {
      this.log.warn(`Isup failed for ${hosts[retry]}/isup.txt, retryamt = ${retry}, hosts: ${hosts.length}`);
      if (hosts[retry + 1]) {
        this.log.warn(`Retrying ${type} host, retry=${retry + 1}`);
        return this.getAvailableHost(type, retry + 1);
      } else {
        this.log.warn(`Isup exhausted for ${type}, falling back to first serverpool host: ${hosts[0]}`);
        return hosts[0];
      }
    }
  }

  public async mkDownloadPubLink(
    tItem: ITransferItemDownload,
    params: ILinkSignParams,
    pltoken?: string,
  ): Promise<string> {
    const baseDomain = await this.requireLinkHost(tItem);
    return baseDomain + this.mkDownloadPubLinkPath(tItem, params, pltoken);
  }

  private async requireLinkHost(tItem: ITransferItemDownload): Promise<string> {
    const host = await this.requireHost(this.linkPoolType(tItem));
    if (tItem.linkpasswordlock && !this.acceptsPasswordlockCookie(host)) {
      this.log.info(`Password-locked link on ${host} relies on server-side pltoken verification`);
    }
    return host;
  }

  private linkPoolType(tItem: ITransferItemDownload): 'web' | 'compat' {
    if (!environment.production) {
      return 'web';
    }
    // Only ceph-middleware verifies `pltoken`; a link on any other backend still needs the
    // passwordlock cookie, which the cookieless compat pool cannot receive.
    if (tItem.linkpasswordlock && tItem.storage_backend !== StorageBackend.CEPH) {
      return 'web';
    }
    return 'compat';
  }

  private acceptsPasswordlockCookie(host: string): boolean {
    return new URL(host).hostname.endsWith(environment.cookiedomain);
  }

  private mkDownloadPubLinkPath(tItem: ITransferItemDownload, params: ILinkSignParams, pltoken?: string): string {
    const qs: string[] = [];
    for (const key in params) {
      qs.push(`${key}=${params[key]}`);
    }
    qs.push('cachekey=' + tItem.cachekey);
    if (pltoken) {
      // URL-safe (base64url + '.' + hex) and not in the signed set — append raw.
      qs.push('pltoken=' + pltoken);
    }
    return ['/p/', encodeURIComponent(tItem.filename), '?', qs.join('&')].join('');
  }
  public getDownloadPubLinkParams(tItem: ITransferItemDownload, rsaDatakey: string, encHost: string): ILinkSignParams {
    /**
     * The weird Content-Disposition line is a requirement due to oddities with
     * UTF-8 names, browsers and HTTP headers requirement to be ascii.
     *
     * When given a file name such as: Naïve file.txt
     *
     * Modern browsers will support rfc5987:
     * Content-Disposition: attachment; filename*=UTF-8''Na%C3%AFve%20file.txt
     *
     * Other browsers (e.g., Safari 5) do their own thing:
     * Content-Disposition: attachment; filename=Naïve file.txt
     *
     * Therefore we combine the two in one-header which has proven to be
     * compatible with all browsers we have tested.
     *
     * Here is some information:
     *
     * - We need to ensure when a browser downloads file, the name remains
     * - The challenge is that HTTP headers can only be ASCII.
     * - Force downloads are done via Content-Disposition attachment
     *   HTTP header.
     *
     * - Therefore, we encodeURI(Naïve file.txt) which results in: Na%C3%AFve%20file.txt
     * - if given to the browser in a content-disposition header, the browser
     *   will literally download the file as "Na%C3%AFve%20file.txt" and not
     *   convert it.
     * - This is why we have the convulted Content-Disposition header.
     *
     * Please only alter it if you've tested it on every browser known to man.
     *  - or just don't edit it.
     *
     * https://stackoverflow.com/questions/93551/how-to-encode-the-filename-parameter-of-content-disposition-header-in-http
     */
    return {
      sharelink_id: tItem.sync_id,
      linkoid: tItem.link_owner_id,
      linkcachekey: tItem.linkID,
      mode: 101,
      datakey: rsaDatakey.replace(/=/g, ''),
      header1: this.base64.encode('Content-Type: ' + tItem.mimetype).replace(/=/g, ''),
      header2: this.base64
        .encode(
          [
            'Content-Disposition: ',
            tItem.disposition,
            '; filename="',
            encodeURIComponent(tItem.filename),
            '";',
            "filename*=UTF-8''",
            encodeURIComponent(tItem.filename),
            ';',
          ].join(''),
        )
        .replace(/=/g, ''),
      uagent: this.digest.hash(navigator.userAgent),
      ipaddress: 's',
      errurl: encHost,
      timestamp: Date.now(),
      engine: `ln-${environment.version}`,
    };
  }

  public async mkPreviewPubUser(tItem: ITransferItemDownload, rsaDatakey: string, timestamp: number): Promise<string> {
    /**
     * The weird Content-Disposition line is a requirement due to oddities with
     * UTF-8 names, browsers and HTTP headers requirement to be ascii.
     *
     * Don't touch unless you read the full comment in getDownloadPubLinkParams()
     *
     * @TODO merge the URL with the output of getDownloadPubLinkParams()
     */
    const domain = await this.requireHost('web');
    return this.decorateUrl([
      domain,
      '/u/',
      encodeURIComponent(tItem.filename),
      '?cachekey=',
      tItem.cachekey,
      '&datakey=',
      rsaDatakey.replace(/=/g, ''),
      '&mode=101',
      '&action=stream',
      '&preview=pdf',
      '&api_version=1',
      '&header1=',
      this.base64.encode('Content-Type: ' + tItem.mimetype).replace(/=/g, ''),
      '&header2=',
      this.base64
        .encode(
          [
            'Content-Disposition: ',
            tItem.disposition,
            '; filename="',
            encodeURIComponent(tItem.filename),
            '";',
            "filename*=UTF-8''",
            encodeURIComponent(tItem.filename),
            ';',
          ].join(''),
        )
        .replace(/=/g, ''),
      '&servtime=',
      timestamp,
    ]);
  }

  public async mkDownloadPubUser(tItem: ITransferItemDownload, rsaDatakey: string, timestamp: number): Promise<string> {
    /**
     * The weird Content-Disposition line is a requirement due to oddities with
     * UTF-8 names, browsers and HTTP headers requirement to be ascii.
     *
     * Don't touch unless you read the full comment in getDownloadPubLinkParams()
     *
     * @TODO merge the URL with the output of getDownloadPubLinkParams()
     */
    const domain = await this.requireHost('web');
    return this.decorateUrl([
      domain,
      '/u/',
      encodeURIComponent(tItem.filename),
      '?cachekey=',
      tItem.cachekey,
      '&datakey=',
      rsaDatakey.replace(/=/g, ''),
      '&mode=101',
      '&api_version=1',
      '&header1=',
      this.base64.encode('Content-Type: ' + tItem.mimetype).replace(/=/g, ''),
      '&header2=',
      this.base64
        .encode(
          [
            'Content-Disposition: ',
            tItem.disposition,
            '; filename="',
            encodeURIComponent(tItem.filename),
            '";',
            "filename*=UTF-8''",
            encodeURIComponent(tItem.filename),
            ';',
          ].join(''),
        )
        .replace(/=/g, ''),
      '&servtime=',
      timestamp,
    ]);
  }

  public async internalDownloadMfsUrl(
    tItem: ITransferItemDownload,
    rsaDatakey: string,
    timestamp: number,
  ): Promise<string> {
    const domain = await this.requireHost('web');
    return this.decorateUrl([
      domain,
      '/u/',
      encodeURIComponent(tItem.filename),
      '?cachekey=',
      tItem.cachekey,
      '&datakey=',
      rsaDatakey.replace(/=/g, ''),
      '&mode=101',
      '&api_version=1',
      '&header1=',
      this.base64.encode('Content-Type: ' + tItem.mimetype).replace(/=/g, ''),
      '&header2=',
      this.base64
        .encode(
          [
            'Content-Disposition: ',
            tItem.disposition,
            '; filename="',
            encodeURIComponent(tItem.filename),
            '";',
            "filename*=UTF-8''",
            encodeURIComponent(tItem.filename),
            ';',
          ].join(''),
        )
        .replace(/=/g, ''),
      '&servtime=',
      timestamp,
    ]);
  }

  public async internalDownloadPubLink(
    tItem: ITransferItemDownload,
    params: ILinkSignParams,
    pltoken?: string,
  ): Promise<string> {
    const hostType = environment.production ? 'compat' : 'web';
    const baseDomain = await this.requireHost(hostType);
    return baseDomain + this.internalDownloadPubLinkPassword(tItem, params, pltoken);
  }

  public internalDownloadPubLinkPassword(
    tItem: ITransferItemDownload,
    params: ILinkSignParams,
    pltoken?: string,
  ): string {
    const qs: string[] = [];
    for (const key in params) {
      qs.push(`${key}=${params[key]}`);
    }
    qs.push('cachekey=' + tItem.cachekey);
    qs.push('nocache=1');
    if (pltoken) {
      qs.push('pltoken=' + pltoken);
    }
    return ['/p/', encodeURIComponent(tItem.filename), '?', qs.join('&')].join('');
  }

  public mkDownloadUrl(tItem: ITransferItemDownload, offset: number, reqLength: number, retry = 0): string {
    const host = this.hosts['web'][retry];
    if (!host) {
      throw new ErrCode(ERROR_CODES.DOWNLOAD_INVALID_RESPONSE, 'No download host available');
    }
    const urlParts = [
      host,
      '/proxyapi.fcgi?command=download',
      '&cachekey=',
      tItem.cachekey,
      '&blobtype=',
      tItem.blobtype,
      '&offset=',
      offset,
    ];
    if (tItem.blobtype == 'btFILE') {
      urlParts.push('&length=' + reqLength);
    }
    return this.decorateUrl(urlParts);
  }

  public mkExtApiUrl(path: string): string {
    return this.decorateUrl([environment.extapihost, `/${path}`, '?']);
  }

  private decorateUrl(urlParts: Array<string | number>): string {
    const url = urlParts.join('');
    const separator = url.includes('?') ? '&' : '?';

    const params = [
      'engine=cp-' + environment.version,
      'userid=' + this.userId,
      'deviceid=' + this.deviceId,
      'devicetypeid=3',
    ];

    return url + separator + params.join('&');
  }

  public gotoHubspotUpgradePage(queryParams): void {
    const queryString = new URLSearchParams(queryParams).toString();
    const redirectUrl = `${environment.hubspotHost}/accountupgrade?${queryString}`;
    window.location.href = redirectUrl;
  }
}
